// TSIA2 player.
//
// The server hands out exactly one module at a time and says only what comes
// next, never why -- so this component cannot leak the routing decision even
// if it wanted to. A student who met the CRC cutoff and one who did not both
// simply receive whatever module is next, and the only visible difference is
// which one arrives.
//
// Forward-only: an answer is written when the student confirms it, the question
// index only ever increases, and there is no navigator or review screen. That
// is the real test's behaviour, not a simplification.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';

import Loader from 'components/Loader';
import axios from 'config/axios';
import { useAppDispatch } from 'hooks/useStore';
import { setError } from 'store/error';

import { TSIA2Directions, TSIA2Finished, TSIA2Saved, TSIA2Welcome } from './Frames';
import { HighlightColor, TSIA2Annotation } from './Highlighter';
import {
    TSIA2ConfirmPanel,
    TSIA2QuestionScreen,
    TSIA2SaveConfirmModal,
    TSIA2SelectAnswerModal,
} from './Question';
import { TSIA2Completion, TSIA2Module, TSIA2Test } from './types';
import './index.css';

type Stage = 'loading' | 'directions' | 'welcome' | 'question' | 'finished' | 'saved';

// The unconfirmed pick and highlights on the question in front of the student,
// kept in this browser so a dropped connection, a closed tab or the browser's
// Back button does not throw them away. Nothing here is an answer: an answer
// is written to the server when the student confirms it, and only then. The
// draft is read back only for a question that still has no confirmed answer,
// and cleared the moment one is confirmed.
type Draft = { choice: number | null; annotations: TSIA2Annotation[] };

const draftKey = (testId: string, questionId: string) => `tsia2-draft:${testId}:${questionId}`;

// localStorage can be missing or throw (private windows, blocked site data);
// the draft is a convenience, so every access fails quietly.
const readDraft = (testId: string, questionId: string): Draft | null => {
    try {
        const raw = window.localStorage.getItem(draftKey(testId, questionId));
        return raw ? JSON.parse(raw) as Draft : null;
    } catch {
        return null;
    }
};

const writeDraft = (testId: string, questionId: string, draft: Draft) => {
    try {
        window.localStorage.setItem(draftKey(testId, questionId), JSON.stringify(draft));
    } catch { /* not available: the draft is best-effort */ }
};

const clearDraft = (testId: string, questionId: string) => {
    try {
        window.localStorage.removeItem(draftKey(testId, questionId));
    } catch { /* nothing to clear */ }
};

const clearAllDrafts = (testId: string) => {
    try {
        const prefix = draftKey(testId, '');
        Object.keys(window.localStorage)
            .filter(key => key.startsWith(prefix))
            .forEach(key => window.localStorage.removeItem(key));
    } catch { /* nothing to clear */ }
};

// Where a resumed module picks up: the first question without a saved answer.
// When every question has one -- the student confirmed the last answer but the
// module was never closed, or used Save & Finish Later on it -- land on the
// last question with its answer pre-selected, so confirming it closes the
// module, rather than on question 1 to re-confirm all of them.
function resumeIndex(module: TSIA2Module) {
    const index = module.questions.findIndex(q => q.savedChoiceIndex === null);
    return index === -1 ? Math.max(module.questions.length - 1, 0) : index;
}

function DigitalTSIA2() {
    const { testId } = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const [test, setTest] = useState<TSIA2Test | null>(null);
    const [module, setModule] = useState<TSIA2Module | null>(null);
    const [stage, setStage] = useState<Stage>('loading');
    const [questionIndex, setQuestionIndex] = useState(0);
    const [selected, setSelected] = useState<number | null>(null);
    const [busy, setBusy] = useState(false);
    const [showSelectAnswer, setShowSelectAnswer] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showSaveConfirm, setShowSaveConfirm] = useState(false);
    // Held here rather than in the question screen: the real test keeps a
    // highlighter colour selected across questions until Stop Highlight.
    const [highlightColor, setHighlightColor] = useState<HighlightColor | null>(null);
    // Highlights for the question on screen. Saved with the answer, so they
    // survive a resume and are there for the review to redraw.
    const [annotations, setAnnotations] = useState<TSIA2Annotation[]>([]);

    const question = module?.questions[questionIndex] ?? null;

    // Which question's draft has been read back. Writes wait for it, so the
    // blank state a question starts with does not overwrite a stored draft
    // before that draft has been restored.
    const draftRestoredFor = useRef<string | null>(null);

    useEffect(() => {
        if (!question || !testId) return;
        draftRestoredFor.current = null;
        if (question.savedChoiceIndex === null) {
            const draft = readDraft(testId, question.questionId);
            if (draft) {
                if (draft.choice !== null) setSelected(draft.choice);
                if (draft.annotations?.length) setAnnotations(draft.annotations);
            }
        }
        draftRestoredFor.current = question.questionId;
    }, [question?.questionId, testId]);  // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!question || !testId || draftRestoredFor.current !== question.questionId) return;
        // A confirmed question needs no draft; the server has the answer.
        if (question.savedChoiceIndex !== null) return;
        writeDraft(testId, question.questionId, { choice: selected, annotations });
    }, [selected, annotations, question, testId]);

    // A module is entered either at its welcome screen (the CRC, which carries
    // the section blurb) or straight at question 1 (the Diagnostic, which must
    // not announce itself). A resumed module skips the welcome.
    const enterModule = useCallback((next: TSIA2Module, resumed: boolean) => {
        setModule(next);
        const startAt = resumed ? resumeIndex(next) : 0;
        setQuestionIndex(startAt);
        setSelected(next.questions[startAt]?.savedChoiceIndex ?? null);
        setAnnotations(next.questions[startAt]?.annotations ?? []);
        const answeredAny = next.questions.some(q => q.savedChoiceIndex !== null);
        setStage(next.prompt && !answeredAny ? 'welcome' : 'question');
    }, []);

    useEffect(() => {
        let cancelled = false;
        axios.get(`tests/digital/tsia2/${testId}/`)
            .then(response => {
                if (cancelled) return;
                const payload: TSIA2Test = response.data;
                setTest(payload);
                if (!payload.module) {
                    if (testId) clearAllDrafts(testId);
                    setStage('finished');
                    return;
                }
                const answeredAny = payload.module.questions.some(q => q.savedChoiceIndex !== null);
                if (answeredAny) {
                    enterModule(payload.module, true);
                } else {
                    // First entry into the whole test: the directions page
                    // comes before the first section's welcome.
                    setModule(payload.module);
                    setQuestionIndex(0);
                    setSelected(null);
                    setStage('directions');
                }
            })
            .catch(error => {
                if (cancelled) return;
                dispatch(setError(error?.response?.data?.message ?? 'Could not load this test.'));
                navigate('/tests');
            });
        return () => { cancelled = true; };
    }, [testId, dispatch, navigate, enterModule]);

    const saveCurrentAnswer = useCallback(async (choiceIndex: number) => {
        if (!module || !question) return;
        await axios.post(`tests/digital/save/tsia2/${testId}/${module.moduleId}/`, {
            answers: [{
                questionId: question.questionId,
                choiceIndex,
                annotations,
            }],
        });
    }, [module, question, testId, annotations]);

    const completeModule = useCallback(async () => {
        if (!module) return;
        const response = await axios.post(
            `tests/digital/tsia2/complete/${testId}/${module.moduleId}/`, {},
        );
        const payload: TSIA2Completion = response.data;
        if (!payload.module) {
            // Every stage is closed: no question can still hold a draft.
            if (testId) clearAllDrafts(testId);
            setStage('finished');
            return;
        }
        enterModule(payload.module, false);
    }, [module, testId, enterModule]);

    const handleSubmit = () => {
        // A save or a stage handoff is in flight: the panel it belongs to
        // must not be reopened over the next module's first question.
        if (busy) return;
        if (selected === null) {
            // First of the three no-skip layers; the other two are the guard
            // below and the server's own check when the module is completed.
            setShowSelectAnswer(true);
            return;
        }
        setShowConfirm(true);
    };

    const handleConfirm = async () => {
        if (selected === null || !module) return;
        setBusy(true);
        try {
            await saveCurrentAnswer(selected);
            // Keep the local copy in step so a resume lands in the right
            // place -- as a new object, not by writing into React state.
            const updated: TSIA2Module = {
                ...module,
                questions: module.questions.map((q, index) => (
                    index === questionIndex
                        ? { ...q, savedChoiceIndex: selected, annotations }
                        : q
                )),
            };
            setModule(updated);
            // Confirmed and on the server: the browser copy has done its job.
            if (testId && question) clearDraft(testId, question.questionId);

            const isLast = questionIndex >= updated.questions.length - 1;
            setShowConfirm(false);
            if (isLast) {
                await completeModule();
                // Whatever was clicked while the handoff was in flight, the
                // next module starts with the panel closed.
                setShowConfirm(false);
            } else {
                const next = questionIndex + 1;
                setQuestionIndex(next);
                setSelected(updated.questions[next]?.savedChoiceIndex ?? null);
                setAnnotations(updated.questions[next]?.annotations ?? []);
            }
        } catch (error: any) {
            dispatch(setError(error?.response?.data?.message ?? 'Could not save your answer.'));
            setShowConfirm(false);
        } finally {
            setBusy(false);
        }
    };

    const handleSaveAndFinishLater = async () => {
        setShowSaveConfirm(false);
        try {
            // An answer is written when the student confirms it, and only
            // then. A selection that was never confirmed is not saved here:
            // saving it would lock the pick in, and the resume would skip
            // past a question the student meant to come back to. If the
            // current question already has a confirmed answer, it is re-sent
            // so highlights made since are kept.
            if (module && question && question.savedChoiceIndex !== null) {
                await saveCurrentAnswer(question.savedChoiceIndex);
            }
            setStage('saved');
        } catch (error: any) {
            dispatch(setError(error?.response?.data?.message ?? 'Could not save your progress.'));
        }
    };

    const handleFinish = async () => {
        setBusy(true);
        try {
            await axios.post(`tests/digital/submit/tsia2/${testId}/`, {});
            navigate(`/report/digital-tsia2/${testId}`);
        } catch (error: any) {
            dispatch(setError(error?.response?.data?.message ?? 'Could not submit this test.'));
            setBusy(false);
        }
    };

    const title = useMemo(() => test?.testName ?? 'TSIA2', [test]);

    if (stage === 'loading' || !test) return <Loader />;

    return (
        <>
            <Helmet><title>{title}</title></Helmet>

            {stage === 'directions' && (
                <TSIA2Directions
                    onBack={() => navigate('/tests')}
                    onStart={() => {
                        if (module) enterModule(module, false);
                    }}
                />
            )}

            {stage === 'welcome' && (
                <TSIA2Welcome
                    prompt={module?.prompt ?? null}
                    onNext={() => setStage('question')}
                />
            )}

            {stage === 'question' && module && question && (
                <>
                    <TSIA2QuestionScreen
                        testName={test.testName}
                        question={question}
                        position={questionIndex + 1}
                        total={module.questionCount}
                        selectedIndex={selected}
                        onSelect={setSelected}
                        onSubmit={handleSubmit}
                        onSaveAndFinishLater={() => { if (!busy) setShowSaveConfirm(true); }}
                        busy={busy}
                        highlightColor={highlightColor}
                        onHighlightColor={setHighlightColor}
                        annotations={annotations}
                        onAnnotationsChange={setAnnotations}
                        confirmPanel={showConfirm ? (
                            <TSIA2ConfirmPanel
                                busy={busy}
                                onConfirm={handleConfirm}
                                onCancel={() => setShowConfirm(false)}
                            />
                        ) : undefined}
                    />
                    {showSelectAnswer && (
                        <TSIA2SelectAnswerModal onClose={() => setShowSelectAnswer(false)} />
                    )}
                    {showSaveConfirm && (
                        <TSIA2SaveConfirmModal
                            onConfirm={handleSaveAndFinishLater}
                            onCancel={() => setShowSaveConfirm(false)}
                        />
                    )}
                </>
            )}

            {stage === 'finished' && <TSIA2Finished busy={busy} onFinish={handleFinish} />}

            {stage === 'saved' && <TSIA2Saved onClose={() => navigate('/tests')} />}
        </>
    );
}

export default DigitalTSIA2;
